import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Image as ImageIcon, Radar, Trash2 } from 'lucide-react';
import type { Api } from '../adapters/Api';
import type { BotSettings, IntelligenceMapTarget, IntelligenceRequest } from '../domain/types';
import type { DlIntelligenceStatus, ShieldWatchEvent } from '../domain/intelligence';
import { canAffordSearch, coveragePercent, shieldEventFamily } from '../domain/intelligence';
import { DOWNLOADABLE_INTELLIGENCE_QUOTA } from '../domain/entitlement';
import { useI18n, type TranslationKey } from '../i18n';
import { EmptyState } from './components';
import { formatRelative } from './status';

// Panel de Intelligence del descargable (doc 13): el usuario busca un jugador o
// alianza en una región y ve los datos ya cacheados por los bots hosted
// (map_targets). Render de resultados CALCADO del hosted (doc 13 §8): tarjetas
// plegables + badge de estado + ordenación Poder/Nombre.
// 0.6.4 (doc 17): economía de tokens — 1 búsqueda gratis/día UTC y las demás se
// pagan con tokens ganados mapeando (RPCs dl_*). La verdad (saldo, gratis,
// costes, cobertura del mapeo) llega en UNA llamada a dl_intelligence_status;
// si aún no responde (backend sin migrar), se cae al conteo local de siempre.

type Translate = ReturnType<typeof useI18n>['t'];

const STATUS_KEY: Record<IntelligenceRequest['status'], TranslationKey> = {
  pending: 'intelligence.statusPending',
  searching_cache: 'intelligence.statusProcessing',
  processing: 'intelligence.statusProcessing',
  completed: 'intelligence.statusCompleted',
  ready: 'intelligence.statusCompleted',
  not_found: 'intelligence.statusNotFound',
  failed: 'intelligence.statusFailed',
  expired: 'intelligence.statusFailed',
  insufficient_data: 'intelligence.statusFailed',
  stale: 'intelligence.statusFailed'
};

const DEFAULT_VISIBLE_INTELLIGENCE_RESULTS = 3; // igual que hosted

function usedTodayCount(requests: IntelligenceRequest[]): number {
  const dayStart = new Date();
  dayStart.setUTCHours(0, 0, 0, 0);
  const start = dayStart.getTime();
  // Cuenta igual que el RPC: requests de hoy (UTC) con status != failed.
  return requests.filter((r) => {
    const at = Date.parse(r.createdAt);
    return Number.isFinite(at) && at >= start && r.status !== 'failed';
  }).length;
}

export function IntelligencePanel({
  api,
  mappingEnabled = false,
  mappingRegionId = null,
  settings,
  onSettingsChange,
  statusCode = null,
  statusParams = null
}: {
  api: Api;
  // 0.6.4: estado del mapeo (extras.intelligence + settings.intel.region_id)
  // para pintar el progreso de la región elegida junto a las búsquedas.
  mappingEnabled?: boolean;
  mappingRegionId?: string | null;
  // 0.6.17 (Shield Monitor): ajustes del bot para elegir modo y objetivo, y
  // el estado que reporta el cliente para pintar la vigilancia en vivo.
  settings?: BotSettings;
  onSettingsChange?: (next: BotSettings) => void;
  statusCode?: string | null;
  statusParams?: Record<string, string | number> | null;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [regionId, setRegionId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [formError, setFormError] = useState('');
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ['intelligenceRequests'],
    queryFn: () => api.listIntelligenceRequests(),
    refetchInterval: 5000
  });

  // Saldo/gratis/costes + cobertura del mapeo en una llamada (doc 17 §6.3).
  // Si el RPC no existe todavía, la query queda en error y el panel cae al
  // conteo local con la constante de entitlement (degradación silenciosa).
  const statusQuery = useQuery({
    queryKey: ['dlIntelligenceStatus', mappingRegionId ?? ''],
    queryFn: () => api.dlIntelligenceStatus(mappingRegionId ?? undefined),
    refetchInterval: 15000,
    retry: false
  });
  const intelStatus = statusQuery.data ?? null;

  const watchMode = settings?.intel?.mode === 'shield_monitor';

  const requests = useMemo(
    () => (query.data ?? []).filter((r) => !r.hiddenAt),
    [query.data]
  );
  const used = usedTodayCount(query.data ?? []);
  // La UI prefiere el valor del RPC; la constante local (1/día en 0.6.4) es
  // solo el fallback mientras no llega.
  const freeLeft = intelStatus ? intelStatus.freeSearchesLeft : Math.max(0, DOWNLOADABLE_INTELLIGENCE_QUOTA - used);
  const freeQuota = intelStatus ? intelStatus.freeSearchesPerDay : DOWNLOADABLE_INTELLIGENCE_QUOTA;

  const submit = useMutation({
    // Jugador vacío = volcado de región completa (doc 13 §9). 0.6.4: las
    // búsquedas MIGRAN a las RPCs dl_* (gratis del día → tokens del ledger).
    mutationFn: (payload: { playerName: string; regionId: string }) =>
      payload.playerName
        ? api.dlSubmitIntelligenceRequest(payload.playerName, payload.regionId)
        : api.dlSubmitRegionRequest(payload.regionId),
    onSuccess: () => {
      setPlayerName('');
      setFormError('');
      queryClient.invalidateQueries({ queryKey: ['intelligenceRequests'] });
      // La búsqueda gasta gratis/tokens: refrescar el saldo del chip.
      queryClient.invalidateQueries({ queryKey: ['dlIntelligenceStatus'] });
    },
    onError: (err) => setFormError(intelligenceError(err, t))
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteIntelligenceRequest(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['intelligenceRequests'] })
  });

  function toggleExpanded(id: string) {
    setExpandedRequests((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onDelete(request: IntelligenceRequest) {
    const name = requestTitle(request, t);
    if (!window.confirm(t('intelligence.deleteConfirm', { name }))) return;
    remove.mutate(request.id);
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const player = playerName.trim();
    if (!regionId.trim()) {
      setFormError(t('intelligence.regionRequired'));
      return;
    }
    // 0 caracteres = región completa; 1-2 = nombre demasiado corto.
    if (player.length > 0 && player.length < 3) {
      setFormError(t('intelligence.minChars'));
      return;
    }
    submit.mutate({ playerName: player, regionId: regionId.trim() });
  }

  const trimmedPlayer = playerName.trim();
  // ¿Puede pagar la búsqueda que va a lanzar? (región completa cuesta más).
  const isRegionSearch = trimmedPlayer.length === 0;
  const canPay = intelStatus ? canAffordSearch(intelStatus, isRegionSearch) : freeLeft > 0;
  const canSubmit =
    !submit.isPending &&
    canPay &&
    !!regionId.trim() &&
    (trimmedPlayer.length === 0 || trimmedPlayer.length >= 3);
  const quotaValue = t('intelligence.quotaRemainingValue', {
    remaining: String(freeLeft),
    limit: String(freeQuota)
  });
  // Chip de la cabecera: saldo de tokens + "{n} gratis hoy" (si queda), con n
  // = free_searches_per_day del RPC (freeQuota ya cae a la constante local si
  // no hay status); sin status (backend antiguo) se mantiene el "X de Y".
  const headerMeta = intelStatus
    ? freeLeft > 0
      ? `${t('intelligence.tokensChip', { n: intelStatus.balance.toLocaleString() })} · ${t('intelligence.freeChip', { n: String(freeQuota) })}`
      : t('intelligence.tokensChip', { n: intelStatus.balance.toLocaleString() })
    : quotaValue;
  // CTA cuando no queda gratis NI saldo ni para la búsqueda barata (doc 17 §7).
  const outOfTokens = !!intelStatus && !canAffordSearch(intelStatus, false);

  return (
    // Sección de acordeón, MISMO patrón/estilo que los módulos (Recolectar,
    // Train troops, Auto Hunt) — como en el hosted: icono de radar + título +
    // cuota "X de Y" en la cabecera + chevron. Va ANTES del panel de Captura.
    <section className="settings-accordion settings-accordion-intelligence">
      <button
        type="button"
        className="settings-accordion-trigger"
        aria-label={t('intelligence.title')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="settings-accordion-icon" aria-hidden="true">
          <Radar size={19} />
        </span>
        <span className="settings-accordion-title">{t('intelligence.title')}</span>
        <span className="dashboard-accordion-meta">{headerMeta}</span>
        <ChevronDown className="settings-accordion-chevron" size={18} aria-hidden="true" />
      </button>
      {open && (
        <div className="settings-accordion-body">
      <form className="intel-form" onSubmit={onSubmit}>
        <label>
          {t('intelligence.region')}
          <input
            type="text"
            value={regionId}
            placeholder={t('intelligence.regionPlaceholder')}
            onChange={(e) => setRegionId(e.target.value)}
          />
        </label>
        <label>
          {t('intelligence.searchPlayer')}
          <input
            type="text"
            value={playerName}
            minLength={3}
            placeholder={t('intelligence.searchPlaceholder')}
            onChange={(e) => setPlayerName(e.target.value)}
          />
        </label>
        <button type="submit" className="primary" disabled={!canSubmit}>
          {submit.isPending ? t('intelligence.requesting') : t('intelligence.request')}
        </button>
      </form>
      <div className="intel-quota">
        <span>{t('intelligence.freeTodayLabel')}</span>
        <strong>{quotaValue}</strong>
      </div>
      {intelStatus && (
        <>
          <div className="intel-quota">
            <span>{t('intelligence.tokensBalance')}</span>
            <strong>{intelStatus.balance.toLocaleString()}</strong>
          </div>
          <p className="muted intel-costs">
            {t('intelligence.costsLine', {
              search: String(intelStatus.searchCost),
              region: String(intelStatus.regionSearchCost)
            })}
          </p>
        </>
      )}
      {outOfTokens && <div className="notice warning">{t('intelligence.noTokensCta')}</div>}
      {/* 0.6.17: la sección es EXCLUYENTE — o mapeo (gana tokens) o vigilancia
          de escudo. Con el monitor activo se oculta el progreso del mapeo
          porque ese bot deja de mapear. */}
      {watchMode ? (
        <ShieldMonitorPanel
          api={api}
          settings={settings}
          onSettingsChange={onSettingsChange}
          statusCode={statusCode}
          statusParams={statusParams}
          t={t}
        />
      ) : (
        <MappingProgress status={intelStatus} enabled={mappingEnabled} regionId={mappingRegionId} t={t} />
      )}
      {settings && onSettingsChange && (
        <label className="intel-watch-toggle">
          <input
            type="checkbox"
            checked={watchMode}
            onChange={(e) => {
              const mode = e.target.checked ? 'shield_monitor' : 'mapping';
              onSettingsChange({
                ...settings,
                intel: { ...settings.intel, mode }
              });
            }}
          />
          <span>{t('shieldWatch.toggle')}</span>
        </label>
      )}
      {formError && <div className="notice warning">{formError}</div>}

      {query.isLoading && <EmptyState>{t('intelligence.loading')}</EmptyState>}

      {!query.isLoading && (requests.length === 0 ? (
        <EmptyState>{t('intelligence.empty')}</EmptyState>
      ) : (
        <div className="intelligence-request-list">
          {requests.map((request) => {
            const expanded = expandedRequests.has(request.id);
            return (
              <article className="intelligence-request-card" key={request.id}>
                <div className="intelligence-request-row">
                  <button
                    type="button"
                    className="intelligence-request-header"
                    aria-expanded={expanded}
                    onClick={() => toggleExpanded(request.id)}
                  >
                    <div className="intelligence-request-title">
                      <span>{t('intelligence.search')}</span>
                      <strong>{requestTitle(request, t)}</strong>
                    </div>
                    <div className="intelligence-request-meta">
                      <span className={`intelligence-status intelligence-status-${request.status}`}>
                        {t(STATUS_KEY[request.status] ?? 'intelligence.statusFailed')}
                      </span>
                      <span>{formatRelative(request.createdAt, t)}</span>
                      {(request.status === 'completed' || request.status === 'ready') && request.results.length > 0 && (
                        <span>{t('intelligence.resultsFound', { count: String(request.results.length) })}</span>
                      )}
                    </div>
                    <ChevronDown className="intelligence-request-chevron" size={17} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon-button intelligence-delete-button"
                    aria-label={t('intelligence.delete')}
                    title={t('intelligence.delete')}
                    onClick={() => onDelete(request)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
                {expanded && (
                  <IntelligenceResultList
                    request={request}
                    expanded={expanded}
                    onToggle={() => toggleExpanded(request.id)}
                    t={t}
                  />
                )}
              </article>
            );
          })}
        </div>
      ))}
        </div>
      )}
    </section>
  );
}

// Progreso del mapeo (0.6.4, doc 17 §7): cobertura de la región elegida en los
// ajustes (barra scanned/total), celdas escaneadas hoy por los bots del usuario
// y tokens ganados hoy (con su tope). Solo se pinta si el usuario usa el mapeo
// (toggle activo o región configurada); así el panel clásico no cambia.
function MappingProgress({
  status,
  enabled,
  regionId,
  t
}: {
  status: DlIntelligenceStatus | null;
  enabled: boolean;
  regionId: string | null;
  t: Translate;
}) {
  if (!enabled && !regionId) return null;
  const region = status?.region && regionId && status.region.regionId === regionId ? status.region : null;
  if (!region) {
    return (
      <div className="intel-mapping">
        <strong className="intel-mapping-title">{t('intelligence.mappingTitle')}</strong>
        <p className="muted">{t('intelligence.mappingNoRegion')}</p>
      </div>
    );
  }
  const pct = coveragePercent(region.cellsScanned, region.cellsTotal);
  return (
    <div className="intel-mapping">
      <strong className="intel-mapping-title">
        {t('intelligence.mappingTitle')} · {region.regionId}
      </strong>
      <div className="intel-mapping-coverage">
        <span>
          {t('intelligence.mappingCells', {
            scanned: region.cellsScanned.toLocaleString(),
            total: region.cellsTotal.toLocaleString()
          })}
        </span>
        <strong>{pct}%</strong>
      </div>
      <div className="intel-progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="intel-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="intel-mapping-metrics">
        <div>
          <span>{t('intelligence.mappingToday')}</span>
          <strong>{region.scannedToday.toLocaleString()}</strong>
        </div>
        {status && (
          <div>
            <span>{t('intelligence.mappingEarnedToday')}</span>
            <strong>
              {/* dailyEarnCap 0 = SIN tope (migración 011, decisión del dueño
                  2026-08-02: mientras más mapea, más gana) → solo el total. */}
              {status.dailyEarnCap > 0
                ? `${status.earnedToday.toLocaleString()} / ${status.dailyEarnCap.toLocaleString()}`
                : status.earnedToday.toLocaleString()}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
}

// -- Shield Monitor (0.6.17) --------------------------------------------------
// Vigila el escudo de UN jugador (coordenadas X/Y de su castillo). Mientras
// está activo, ese bot NO mapea: solo comprueba, cada intervalo y aprovechando
// sus huecos libres, si el objetivo conserva la cúpula. En cuanto deja de
// tenerla (confirmado dos veces seguidas) salta el aviso.
// Historial de la vigilancia: sin esto el panel solo mostraba el instante
// actual, así que no se podía ver cuándo cayó el escudo ni cuándo volvió
// (petición del dueño 2026-08-03). Se listan CAMBIOS de estado, no cada
// comprobación: con un chequeo por minuto lo segundo sería ruido.
function ShieldWatchHistory({
  api,
  t
}: {
  api: Api;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
}) {
  const query = useQuery({
    queryKey: ['shieldWatchHistory'],
    queryFn: () => api.dlShieldWatchHistory(12),
    refetchInterval: 20000,
    retry: false
  });
  // Solo la familia 'watch': los ataques a la ciudad (Auto Shield) viven en el
  // feed de Actividad de la página de flota, no en el historial de vigilancia.
  const events = (query.data ?? []).filter((e) => shieldEventFamily(e.state) === 'watch');
  // PRUEBA VISUAL (0.6.27): el frame con el que el bot decidió. No viaja en el
  // historial (se sondea cada 20 s y serían cientos de KB por vuelta): se pide
  // solo al pulsar. Únicamente los 5 últimos eventos de cada bot la conservan.
  const [shown, setShown] = useState<ShieldWatchEvent | null>(null);
  const capture = useQuery({
    queryKey: ['shieldWatchCapture', shown?.instanceId, shown?.stamp, shown?.state],
    queryFn: () => api.dlShieldWatchCapture(shown!.instanceId, shown!.stamp, shown!.state),
    enabled: !!shown,
    staleTime: 5 * 60000,
    retry: false
  });
  if (!events.length) return null;

  const label: Record<string, { cls: string; key: TranslationKey }> = {
    shielded: { cls: 'ok', key: 'shieldWatch.stateShielded' },
    confirming: { cls: 'warn', key: 'shieldWatch.stateConfirming' },
    down: { cls: 'down', key: 'shieldWatch.stateDown' },
    moved: { cls: 'warn', key: 'shieldWatch.stateMoved' },
    unknown: { cls: 'warn', key: 'shieldWatch.stateNoDetector' }
  };

  return (
    <div className="intel-watch-history">
      <strong>{t('shieldWatch.historyTitle')}</strong>
      <ul>
        {events.map((event, index) => {
          const meta = label[event.state] ?? label.unknown;
          return (
            <li key={`${event.at}-${index}`}>
              <span className={`intel-watch-dot intel-watch-${meta.cls}`} aria-hidden="true" />
              <span className="intel-watch-history-state">{t(meta.key)}</span>
              {event.x !== null && event.y !== null && (
                <span className="muted">({event.x},{event.y})</span>
              )}
              {event.notified && <span className="intel-watch-mailed">✉</span>}
              {event.hasCapture && (
                <button
                  type="button"
                  className="intel-watch-shot"
                  onClick={() => setShown(event)}
                  title={t('shieldWatch.captureOpen')}
                  aria-label={t('shieldWatch.captureOpen')}
                >
                  <ImageIcon size={14} aria-hidden="true" />
                </button>
              )}
              <time>{formatRelative(event.at, t)}</time>
            </li>
          );
        })}
      </ul>
      {shown && (
        <div
          className="intel-watch-viewer"
          role="dialog"
          aria-modal="true"
          onClick={() => setShown(null)}
        >
          <figure onClick={(e) => e.stopPropagation()}>
            <figcaption>
              {t(label[shown.state]?.key ?? 'shieldWatch.stateNoDetector')}
              {shown.x !== null && shown.y !== null && ` (${shown.x},${shown.y})`}
              {' · '}
              {formatRelative(shown.at, t)}
            </figcaption>
            {capture.isLoading && <p className="muted">{t('shieldWatch.captureLoading')}</p>}
            {!capture.isLoading && capture.data && (
              <img src={capture.data} alt={t('shieldWatch.captureAlt')} />
            )}
            {!capture.isLoading && !capture.data && (
              <p className="muted">{t('shieldWatch.captureGone')}</p>
            )}
            <button type="button" onClick={() => setShown(null)}>
              {t('shieldWatch.captureClose')}
            </button>
          </figure>
        </div>
      )}
    </div>
  );
}

function ShieldMonitorPanel({
  api,
  settings,
  onSettingsChange,
  statusCode,
  statusParams,
  t
}: {
  api: Api;
  settings?: BotSettings;
  onSettingsChange?: (next: BotSettings) => void;
  statusCode: string | null;
  statusParams: Record<string, string | number> | null;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
}) {
  if (!settings || !onSettingsChange) return null;
  const watch = settings.intel.watch ?? { x: null, y: null, region: null, interval_s: 300 };

  function patchWatch(next: Partial<{ x: number | null; y: number | null; region: string | null; interval_s: number }>) {
    onSettingsChange!({
      ...settings!,
      intel: { ...settings!.intel, watch: { ...watch, ...next } }
    });
  }

  const coordValue = (v: number | null) => (v === null || Number.isNaN(v) ? '' : String(v));
  const parseCoord = (raw: string): number | null => {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 && n <= 1200 ? n : null;
  };

  // Estado en vivo: lo reporta el cliente por status_code (la web solo traduce).
  const live = (() => {
    switch (statusCode) {
      case 'shield_watch_shielded':
        return { cls: 'ok', text: t('shieldWatch.stateShielded') };
      case 'shield_watch_confirming':
        return { cls: 'warn', text: t('shieldWatch.stateConfirming') };
      case 'shield_watch_down':
        return { cls: 'down', text: t('shieldWatch.stateDown') };
      case 'shield_watch_moved':
        return { cls: 'warn', text: t('shieldWatch.stateMoved') };
      case 'shield_watch_detector_missing':
        return { cls: 'warn', text: t('shieldWatch.stateNoDetector') };
      default:
        return null;
    }
  })();
  const targetName = statusParams?.name ? String(statusParams.name) : null;

  return (
    <div className="intel-mapping intel-shieldwatch">
      <div className="intel-mapping-head">
        <strong>{t('shieldWatch.title')}</strong>
      </div>
      <p className="muted">{t('shieldWatch.help')}</p>
      <div className="intel-watch-coords">
        <label>
          {t('shieldWatch.region')}
          <input
            type="text" inputMode="numeric" maxLength={5}
            placeholder={settings.intel.region_id ?? ''}
            value={watch.region ?? ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              patchWatch({ region: digits || null });
            }}
          />
        </label>
        <label>
          X
          <input
            type="number" min={0} max={1200} inputMode="numeric"
            value={coordValue(watch.x)}
            onChange={(e) => patchWatch({ x: parseCoord(e.target.value) })}
          />
        </label>
        <label>
          Y
          <input
            type="number" min={0} max={1200} inputMode="numeric"
            value={coordValue(watch.y)}
            onChange={(e) => patchWatch({ y: parseCoord(e.target.value) })}
          />
        </label>
        <label>
          {t('shieldWatch.interval')}
          <select
            value={String(watch.interval_s ?? 300)}
            onChange={(e) => patchWatch({ interval_s: Number.parseInt(e.target.value, 10) })}
          >
            <option value="60">1 min</option>
            <option value="300">5 min</option>
            <option value="600">10 min</option>
            <option value="1800">30 min</option>
          </select>
        </label>
      </div>
      {(watch.x === null || watch.y === null) && (
        <div className="notice warning">{t('shieldWatch.needCoords')}</div>
      )}
      {live && (
        <div className={`intel-watch-state intel-watch-${live.cls}`}>
          <span>{live.text}</span>
          {targetName && <strong>{targetName}</strong>}
        </div>
      )}
      <ShieldWatchHistory api={api} t={t} />
      <p className="muted intel-costs">{t('shieldWatch.idleNote')}</p>
    </div>
  );
}

function IntelligenceResultList({
  request,
  expanded,
  onToggle,
  t
}: {
  request: IntelligenceRequest;
  expanded: boolean;
  onToggle: () => void;
  t: Translate;
}) {
  const [sortBy, setSortBy] = useState<'power' | 'name'>('power');

  if (request.status !== 'completed' && request.status !== 'ready') {
    return (
      <span className="intelligence-result-muted">
        {t(STATUS_KEY[request.status] ?? 'intelligence.statusFailed')}
      </span>
    );
  }
  if (!request.results.length) {
    return <span className="intelligence-result-muted">{t('intelligence.notFound')}</span>;
  }

  const sortedResults = sortIntelligenceResults(request.results, sortBy);
  const visibleResults = expanded ? sortedResults : sortedResults.slice(0, DEFAULT_VISIBLE_INTELLIGENCE_RESULTS);
  const hiddenCount = sortedResults.length - visibleResults.length;

  return (
    <div className="intelligence-results">
      <div className="intelligence-result-toolbar" aria-label={t('intelligence.sortResults')}>
        <span>{t('intelligence.sortResults')}</span>
        <div className="segmented-control">
          <button type="button" className={sortBy === 'power' ? 'active' : ''} onClick={() => setSortBy('power')}>
            {t('intelligence.sortPower')}
          </button>
          <button type="button" className={sortBy === 'name' ? 'active' : ''} onClick={() => setSortBy('name')}>
            {t('intelligence.sortName')}
          </button>
        </div>
      </div>
      <div className="intelligence-result-list">
        {visibleResults.map((result, index) => (
          <div
            className="intelligence-result-item"
            key={`${result.playerName || 'target'}-${result.castleX ?? 'x'}-${result.castleY ?? 'y'}-${index}`}
          >
            <div className="intelligence-result-main">
              <strong>{result.playerName || request.playerName || '-'}</strong>
              <span>{formatAlliance(result) || '-'}</span>
            </div>
            <div className="intelligence-result-meta">
              {targetMetaItems(result, t).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {(hiddenCount > 0 || expanded) && (
        <button type="button" className="link-button intelligence-more" onClick={onToggle}>
          {expanded ? t('intelligence.showLess') : t('intelligence.showMore', { count: String(hiddenCount) })}
        </button>
      )}
    </div>
  );
}

// Título de la tarjeta: nombre buscado, o "Toda la región N" en los volcados
// de región (jugador vacío). Se detecta por query_type y, como red para filas
// creadas antes de exponer la columna, por playerName vacío con región.
function requestTitle(request: IntelligenceRequest, t: Translate): string {
  const isRegionSearch = request.queryType === 'region' || (!request.playerName && !!request.regionId);
  if (isRegionSearch) {
    return t('intelligence.regionSearchTitle', { region: request.regionId ?? '-' });
  }
  return request.playerName || '-';
}

function sortIntelligenceResults(results: IntelligenceRequest['results'], sortBy: 'power' | 'name') {
  const items = [...results];
  if (sortBy === 'name') {
    return items.sort((a, b) =>
      String(a.playerName || '').localeCompare(String(b.playerName || ''), undefined, { sensitivity: 'base' })
    );
  }
  return items.sort((a, b) => {
    const powerDelta = (b.power ?? -1) - (a.power ?? -1);
    if (powerDelta !== 0) return powerDelta;
    return String(a.playerName || '').localeCompare(String(b.playerName || ''), undefined, { sensitivity: 'base' });
  });
}

function formatAlliance(result: IntelligenceMapTarget): string {
  return [result.allianceTag ? `[${result.allianceTag}]` : '', result.allianceName || ''].filter(Boolean).join(' ');
}

function targetMetaItems(result: IntelligenceMapTarget, t: Translate): string[] {
  return [
    result.power != null ? `${t('intelligence.power')} ${result.power.toLocaleString()}` : '',
    result.castleX != null && result.castleY != null
      ? `${t('intelligence.coordinates')} ${result.castleX}, ${result.castleY}`
      : '',
    result.lastSeenAt ? `${t('intelligence.lastSeen')} ${formatRelative(result.lastSeenAt, t)}` : ''
  ].filter(Boolean);
}

// Traduce los mensajes de error del RPC a claves i18n útiles.
function intelligenceError(err: unknown, t: Translate): string {
  const message = err instanceof Error ? err.message : String(err);
  // 0.6.4: sin gratis del día y sin tokens suficientes (RPCs dl_*).
  if (/insufficient[\s_]tokens|not enough tokens/i.test(message)) return t('intelligence.errorTokens');
  if (/quota exceeded/i.test(message)) return t('intelligence.errorQuota');
  if (/already pending/i.test(message)) return t('intelligence.errorPending');
  if (/unknown region/i.test(message)) return t('intelligence.errorUnknownRegion');
  if (/no eligible/i.test(message)) return t('intelligence.errorNoRegion');
  return t('intelligence.errorGeneric');
}
